// app/src/Avatar.jsx — a person's avatar: their uploaded profile photo, or a
// colored initials circle as fallback (also used if the image fails to load).
function Avatar({ name, url, size = 32 }) {
  const [bad, setBad] = React.useState(false);
  React.useEffect(() => { setBad(false); }, [url]); // a new url gets a fresh chance
  const initial = ((name || '?').trim().slice(0, 1) || '?').toUpperCase();
  const base = { width: size, height: size, borderRadius: '50%', flex: 'none', display: 'block' };
  if (url && !bad) {
    return <img src={url} alt={name || ''} onError={() => setBad(true)}
      style={{ ...base, objectFit: 'cover', border: '1px solid var(--line)', background: 'var(--surface-2)' }} />;
  }
  return (
    <div aria-hidden="true" style={{
      ...base, background: 'var(--primary-soft)', color: 'var(--primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: Math.round(size * 0.42),
    }}>{initial}</div>
  );
}

// AvatarCropper — a dependency-free crop modal. Shows the picked image in a square
// viewport with a circular guide; the user pans (drag) and zooms (slider) to frame,
// then we render the framed square to a 512×512 canvas and hand back a JPEG blob.
// (The square is displayed round everywhere by <Avatar>, so the circle is the guide.)
function AvatarCropper({ file, onCancel, onCropped }) {
  const VIEW = 280;   // on-screen viewport (px)
  const OUT = 512;    // exported image (px)
  const [img, setImg] = React.useState(null);
  const [scale, setScale] = React.useState(1);
  const [minScale, setMinScale] = React.useState(1);
  const [pos, setPos] = React.useState({ x: 0, y: 0 }); // image top-left in viewport coords
  const [busy, setBusy] = React.useState(false);
  const drag = React.useRef(null);

  React.useEffect(() => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => {
      const ms = Math.max(VIEW / im.naturalWidth, VIEW / im.naturalHeight); // "cover"
      setImg(im); setMinScale(ms); setScale(ms);
      setPos({ x: (VIEW - im.naturalWidth * ms) / 2, y: (VIEW - im.naturalHeight * ms) / 2 });
    };
    im.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const clamp = (p, s) => {
    if (!img) return p;
    const w = img.naturalWidth * s, h = img.naturalHeight * s;
    return { x: Math.min(0, Math.max(VIEW - w, p.x)), y: Math.min(0, Math.max(VIEW - h, p.y)) };
  };
  const pt = (e) => { const t = e.touches ? e.touches[0] : e; return { x: t.clientX, y: t.clientY }; };
  const onScale = (s) => {
    const c = VIEW / 2, k = s / scale;
    setPos((p) => clamp({ x: c - (c - p.x) * k, y: c - (c - p.y) * k }, s));
    setScale(s);
  };
  const startDrag = (e) => { const p = pt(e); drag.current = { px: p.x, py: p.y, ox: pos.x, oy: pos.y }; };
  const moveDrag = (e) => { if (!drag.current) return; const p = pt(e); setPos(clamp({ x: drag.current.ox + (p.x - drag.current.px), y: drag.current.oy + (p.y - drag.current.py) }, scale)); };
  const endDrag = () => { drag.current = null; };

  const confirm = async () => {
    if (!img || busy) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUT; canvas.height = OUT;
      const ctx = canvas.getContext('2d');
      const r = OUT / VIEW;
      ctx.drawImage(img, pos.x * r, pos.y * r, img.naturalWidth * scale * r, img.naturalHeight * scale * r);
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
      if (blob) onCropped(blob); else onCancel();
    } catch { onCancel(); } finally { setBusy(false); }
  };

  const btn = { flex: 1, padding: 11, borderRadius: 'var(--radius-sm)', fontWeight: 700, fontSize: 14, cursor: 'pointer' };
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(20,12,8,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 340, background: 'var(--surface)', borderRadius: 18, padding: 18 }}>
        <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 16, color: 'var(--ink)', marginBottom: 12, textAlign: 'center' }}>{t('avatar.cropTitle', 'Position your photo')}</div>
        <div style={{ position: 'relative', width: VIEW, height: VIEW, margin: '0 auto', overflow: 'hidden', borderRadius: 12, background: '#000', touchAction: 'none', cursor: 'grab' }}
          onMouseDown={startDrag} onMouseMove={moveDrag} onMouseUp={endDrag} onMouseLeave={endDrag}
          onTouchStart={startDrag} onTouchMove={moveDrag} onTouchEnd={endDrag}>
          {img && <img src={img.src} draggable={false} alt="" style={{ position: 'absolute', left: pos.x, top: pos.y, width: img.naturalWidth * scale, height: img.naturalHeight * scale, maxWidth: 'none', userSelect: 'none' }} />}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', boxShadow: '0 0 0 9999px rgba(20,12,8,.55)', pointerEvents: 'none' }} />
        </div>
        <input type="range" min={minScale} max={minScale * 4} step="any" value={scale} onChange={(e) => onScale(Number(e.target.value))} aria-label={t('avatar.zoom', 'Zoom')} style={{ width: VIEW, display: 'block', margin: '14px auto 16px' }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancel} style={{ ...btn, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)' }}>{t('common.cancel', 'Cancel')}</button>
          <button onClick={confirm} disabled={busy} style={{ ...btn, border: 'none', background: 'var(--primary)', color: 'var(--primary-ink)', opacity: busy ? 0.6 : 1 }}>{busy ? t('common.saving', 'Saving…') : t('avatar.use', 'Use photo')}</button>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { Avatar, AvatarCropper });
